import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getIntroVideo,
  getPaymentSecret,
  sendOtp,
  resendOtp,
  verifyOtp,
} from "../apiconfig/api";
interface IntroVideo {
  data: {
    employer: {
      introVideoUuid: string;
      slug: string;
      uuid: string;
      companyName: string;
      about: string;
      companyAddress: string;
      cardToken: string | null;
      createdAt: string;
      deletedAt: string | null;
    }
  };
}

interface TempURL {
  fileName: string;
  contentType: string;
}

interface IntroVideoState {
  video: IntroVideo | null;
  mergedVideoBlob: Blob | null;
  temporaryURL: TempURL | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  secret: any | null;
  otp: any | null;
  error: string | null;
}

export const fetchIntroVideo = createAsyncThunk<IntroVideo, string | number>(
  "introVideo/fetch",
  async (id) => {
    const response = await getIntroVideo(id);
    return response;
  }
);

export const fetchPaymentSecret = createAsyncThunk<
  any,
  any,
  { rejectValue: string }
>("payment/secret", async (payload, { rejectWithValue }) => {
  try {
    const response = await getPaymentSecret(payload);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const sendOTPThunk = createAsyncThunk<any, any, { rejectValue: string }>(
  "otp/send",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await sendOtp(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const verifyOTPThunk = createAsyncThunk<any, any, { rejectValue: string }>(
  "otp/verify",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await verifyOtp(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const resendOTPThunk = createAsyncThunk<any, any, { rejectValue: string }>(
  "otp/resend",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await resendOtp(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState: IntroVideoState = {
  video: null,
  mergedVideoBlob: null,
  temporaryURL: null,
  status: "idle",
  secret: null,
  otp: null,
  error: null,
};

const introVideoSlice = createSlice({
  name: "introVideo",
  initialState,
  reducers: {
    setMergedVideoBlob: (state, action: PayloadAction<Blob>) => {
      state.mergedVideoBlob = action.payload;
    },
    clearMergedVideoBlob: (state) => {
      state.mergedVideoBlob = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIntroVideo.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        fetchIntroVideo.fulfilled,
        (state, action: PayloadAction<IntroVideo>) => {
          state.status = "succeeded";
          state.video = action.payload;
        }
      )
      .addCase(fetchIntroVideo.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to fetch intro video";
      })
      .addCase(fetchPaymentSecret.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        fetchPaymentSecret.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.status = "succeeded";
          state.secret = action.payload;
        }
      )
      .addCase(fetchPaymentSecret.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch temporary URL";
      })
      .addCase(sendOTPThunk.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(sendOTPThunk.fulfilled, (state, action: PayloadAction<any>) => {
        state.status = "succeeded";
        state.otp = action.payload;
      })
      .addCase(sendOTPThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to send OTP";
      })
      .addCase(verifyOTPThunk.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(verifyOTPThunk.fulfilled, (state, action: PayloadAction<any>) => {
        state.status = "succeeded";
        state.otp = action.payload;
      })
      .addCase(verifyOTPThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to verify OTP";
      })
      .addCase(resendOTPThunk.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(resendOTPThunk.fulfilled, (state, action: PayloadAction<any>) => {
        state.status = "succeeded";
        state.otp = action.payload;
      })
      .addCase(resendOTPThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to resend OTP";
      });
  },
});

export const { setMergedVideoBlob, clearMergedVideoBlob } =
  introVideoSlice.actions;

export default introVideoSlice.reducer;
